import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { api } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useVisitorAlerts } from "@/components/VisitorAlerts";
import { toast } from "sonner";

const OPERATOR_ROLES = ["sales_manager", "admin", "super_admin"];
const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" });

export function TodayChatCard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { youOnline, refresh } = useVisitorAlerts();
  const isOperator = OPERATOR_ROLES.includes(user?.role);
  const [card, setCard] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/visitor-chat/today-card"); setCard(data); } catch { setCard(null); }
  }, []);

  useEffect(() => {
    if (!isOperator) return undefined;
    load();
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [isOperator, load]);

  if (!isOperator || !card?.enabled) return null;
  const newest = card.items?.[0];

  const send = async (e) => {
    e.preventDefault();
    if (!newest || !reply.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/visitor-chat/${newest.id}/reply`, { text: reply.trim() });
      setReply("");
      toast.success(`Reply sent to Visitor #${newest.visitor_number}`);
      load();
      refresh?.();
    } catch { toast.error("Reply could not be sent"); }
    finally { setSending(false); }
  };

  return (
    <section className="mt-5 rounded-xl border border-slate-200 bg-white p-4" data-testid="today-chat-card">
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-teal-50 text-teal-700"><MessageCircle className="h-4 w-4" /></span>
        <h2 className="whitespace-nowrap font-heading text-sm font-bold text-slate-800">Website chats</h2>
        <span data-testid="today-chat-unread" className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${card.unread_conversations ? "bg-rose-600 text-white" : "bg-slate-100 text-slate-500"}`}>
          {card.unread_conversations} unread
        </span>
        {youOnline && <span data-testid="today-chat-you-online" className="basis-full text-xs font-medium text-teal-700 sm:ml-auto sm:basis-auto">You're online to website chats</span>}
      </div>
      {!card.items?.length ? <p className="mt-2 text-xs text-slate-400">No website conversations yet.</p> : (
        <div className="mt-3 space-y-1.5">
          {card.items.map((c) => (
            <button key={c.id} type="button" data-testid={`today-chat-item-${c.id}`} onClick={() => navigate("/visitors")}
              className={`flex min-h-[44px] w-full items-center gap-2 rounded-lg border border-slate-100 px-3 text-left text-sm hover:bg-slate-50 ${c.unread ? "font-semibold text-slate-900" : "text-slate-600"}`}>
              {c.unread && <span aria-label="Unread" className="h-2 w-2 shrink-0 rounded-full bg-rose-600" />}
              <span className="truncate">Visitor #{c.visitor_number}</span>
              {c.offline_message && <span className="shrink-0 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">Offline message</span>}
              <span className="ml-auto shrink-0 text-xs font-normal text-slate-400">{fmt(c.updated_at)}</span>
            </button>
          ))}
        </div>
      )}
      {newest && (
        <form onSubmit={send} className="mt-3 flex gap-2" data-testid="today-chat-quick-reply">
          <input aria-label={`Quick reply to Visitor #${newest.visitor_number}`} data-testid="today-chat-reply-input" maxLength={2000}
            value={reply} onChange={(e) => setReply(e.target.value)} placeholder={`Reply to Visitor #${newest.visitor_number}`}
            className="min-h-[44px] min-w-0 flex-1 rounded-lg border border-slate-200 px-3 text-sm" />
          <button type="submit" data-testid="today-chat-reply-send" disabled={sending || !reply.trim()}
            className="min-h-[44px] rounded-lg bg-teal-700 px-4 text-sm font-semibold text-white disabled:opacity-50">{sending ? "Sending…" : "Reply"}</button>
        </form>
      )}
      <button type="button" data-testid="today-chat-open-inbox" onClick={() => navigate("/visitors")}
        className="mt-3 min-h-[44px] w-full rounded-lg border border-teal-200 text-sm font-semibold text-teal-700 hover:bg-teal-50">Open inbox</button>
    </section>
  );
}
